import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Lock, LogOut, Eye, Check, X, ChevronLeft } from "lucide-react";
import { useLang } from "../context/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "intimo_admin_token";

export function AdminLogin() {
  const { t } = useLang();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(TOKEN_KEY)) nav("/admin/dashboard");
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      nav("/admin/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-10">
          <Lock size={20} className="text-[#00E5FF]" />
          <div className="label-eyebrow text-[#00E5FF]">{t.admin.loginTitle}</div>
        </div>
        <h1 className="font-display uppercase text-4xl text-white mb-3 tracking-tight">INTIMO MODELS</h1>
        <p className="text-neutral-500 text-sm mb-10">{t.admin.loginSubtitle}</p>
        <form onSubmit={submit} className="space-y-6">
          <div>
            <label className="intimo-label">{t.admin.email}</label>
            <input
              data-testid="admin-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="intimo-input"
              required
            />
          </div>
          <div>
            <label className="intimo-label">{t.admin.password}</label>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="intimo-input"
              required
            />
          </div>
          {error && <div data-testid="admin-login-error" className="text-[#FF3366] text-sm">{error}</div>}
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="btn-solid w-full justify-center"
          >
            {loading ? "..." : `${t.admin.signIn} →`}
          </button>
        </form>
        <div className="mt-10 text-center">
          <a href="/" className="text-xs text-neutral-600 hover:text-[#00E5FF] tracking-[0.2em] uppercase">← Back to site</a>
        </div>
      </div>
    </div>
  );
}

export function AdminDashboard() {
  const { t } = useLang();
  const nav = useNavigate();
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const token = localStorage.getItem(TOKEN_KEY);

  useEffect(() => {
    if (!token) { nav("/admin"); return; }
    load();
  // eslint-disable-next-line
  }, [filter]);

  const load = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [appsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/applications${filter ? `?status=${filter}` : ""}`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setApps(appsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem(TOKEN_KEY); nav("/admin"); }
    } finally { setLoading(false); }
  };

  const logout = () => { localStorage.removeItem(TOKEN_KEY); nav("/admin"); };

  const updateStatus = async (id, status) => {
    const headers = { Authorization: `Bearer ${token}` };
    const fd = new FormData();
    fd.append("status", status);
    await axios.patch(`${API}/admin/applications/${id}`, fd, { headers });
    load();
    if (selected?.id === id) setSelected({ ...selected, status });
  };

  if (selected) {
    return <AppDetail app={selected} token={token} onBack={() => setSelected(null)} onUpdate={updateStatus} />;
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/5 px-6 lg:px-10 py-5 flex items-center justify-between sticky top-0 bg-[#050505]/90 backdrop-blur z-40">
        <div className="flex items-center gap-3">
          <div className="font-display tracking-[0.25em] text-lg">INTIMO<span className="text-[#00E5FF]">.</span>ADMIN</div>
        </div>
        <button data-testid="admin-logout" onClick={logout} className="btn-ghost inline-flex items-center gap-2">
          <LogOut size={14} /> {t.admin.logout}
        </button>
      </header>

      <div className="px-6 lg:px-10 py-10 max-w-7xl mx-auto">
        <div className="mb-12">
          <div className="label-eyebrow text-[#00E5FF] mb-3">{t.admin.dashboard}</div>
          <h1 className="font-display uppercase text-4xl lg:text-5xl tracking-tight">{t.admin.applications}</h1>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 mb-10">
          {[
            { k: "total", v: stats.total, label: t.admin.total, color: "text-white" },
            { k: "pending", v: stats.pending, label: t.admin.pending, color: "text-yellow-400" },
            { k: "approved", v: stats.approved, label: t.admin.approved, color: "text-[#00E5FF]" },
            { k: "rejected", v: stats.rejected, label: t.admin.rejected, color: "text-[#FF3366]" },
          ].map((s) => (
            <button
              key={s.k}
              data-testid={`stat-${s.k}`}
              onClick={() => setFilter(s.k === "total" ? "" : s.k)}
              className={`bg-[#050505] p-8 text-left hover:bg-[#0a0a0a] transition-colors ${filter === (s.k === "total" ? "" : s.k) ? "ring-1 ring-[#00E5FF]" : ""}`}
            >
              <div className="label-eyebrow mb-3">{s.label}</div>
              <div className={`font-display text-5xl ${s.color}`}>{s.v}</div>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-neutral-500 text-center py-20">Loading...</div>
        ) : apps.length === 0 ? (
          <div data-testid="no-apps" className="text-neutral-500 text-center py-20 border border-white/5">{t.admin.noApps}</div>
        ) : (
          <div className="border border-white/5 overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0a0a0a]">
                <tr className="text-left">
                  <Th>{t.admin.name}</Th>
                  <Th>{t.admin.ageCol}</Th>
                  <Th>{t.admin.country}</Th>
                  <Th>{t.admin.bodyType}</Th>
                  <Th>{t.admin.status}</Th>
                  <Th>{t.admin.submitted}</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {apps.map((a) => (
                  <tr key={a.id} data-testid={`app-row-${a.id}`} className="border-t border-white/5 hover:bg-[#0a0a0a] transition-colors">
                    <td className="px-5 py-4 text-white">{a.full_name}<div className="text-xs text-neutral-500">{a.email}</div></td>
                    <td className="px-5 py-4 text-neutral-300">{a.age}</td>
                    <td className="px-5 py-4 text-neutral-300">{a.country}</td>
                    <td className="px-5 py-4 text-neutral-300">{a.body_type}</td>
                    <td className="px-5 py-4"><StatusBadge status={a.status} /></td>
                    <td className="px-5 py-4 text-neutral-500 text-sm">{new Date(a.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-4 text-right">
                      <button data-testid={`view-${a.id}`} onClick={() => setSelected(a)} className="btn-ghost inline-flex items-center gap-2 text-xs">
                        <Eye size={12} /> {t.admin.view}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const Th = ({ children }) => (
  <th className="px-5 py-4 text-[0.7rem] font-bold tracking-[0.2em] uppercase text-neutral-500">{children}</th>
);

const StatusBadge = ({ status }) => {
  const cls = status === "approved" ? "badge-approved" : status === "rejected" ? "badge-rejected" : "badge-pending";
  return <span className={`${cls} text-[0.65rem] font-bold tracking-[0.15em] uppercase px-2.5 py-1`}>{status}</span>;
};

function AppDetail({ app, token, onBack, onUpdate }) {
  const { t } = useLang();
  const [imgs, setImgs] = useState({ face: "", boxer_front: "", boxer_back: "", slippers_front: "", slippers_back: "" });

  useEffect(() => {
    const load = async () => {
      const headers = { Authorization: `Bearer ${token}` };
      const out = {};
      for (const [kind, path] of [
        ["face", app.photo_face],
        ["boxer_front", app.photo_boxer_front],
        ["boxer_back", app.photo_boxer_back],
        ["slippers_front", app.photo_slippers_front],
        ["slippers_back", app.photo_slippers_back],
      ]) {
        if (!path) { out[kind] = ""; continue; }
        try {
          const res = await axios.get(`${API}/files/${path}`, { headers, responseType: "blob" });
          out[kind] = URL.createObjectURL(res.data);
        } catch (e) { out[kind] = ""; }
      }
      setImgs(out);
    };
    load();
    return () => Object.values(imgs).forEach((u) => u && URL.revokeObjectURL(u));
  // eslint-disable-next-line
  }, [app.id]);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/5 px-6 lg:px-10 py-5 flex items-center justify-between sticky top-0 bg-[#050505]/90 backdrop-blur z-40">
        <button data-testid="back-to-list" onClick={onBack} className="btn-ghost inline-flex items-center gap-2">
          <ChevronLeft size={14} /> {t.admin.back}
        </button>
        <div className="flex items-center gap-3">
          <StatusBadge status={app.status} />
          <button data-testid="approve-btn" onClick={() => onUpdate(app.id, "approved")} className="btn-cyan text-xs px-4 py-2 inline-flex items-center gap-2">
            <Check size={14} /> {t.admin.approve}
          </button>
          <button data-testid="reject-btn" onClick={() => onUpdate(app.id, "rejected")} className="btn-ghost text-xs px-4 py-2 inline-flex items-center gap-2 hover:!border-[#FF3366] hover:!text-[#FF3366]">
            <X size={14} /> {t.admin.reject}
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <div className="mb-10">
          <div className="label-eyebrow text-[#00E5FF] mb-3">{t.admin.details}</div>
          <h1 className="font-display uppercase text-4xl lg:text-5xl tracking-tight">{app.full_name}</h1>
          <p className="text-neutral-500 mt-2">{app.artistic_name && `"${app.artistic_name}" · `}{app.age} · {app.nationality}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 space-y-8">
            <Info label={t.admin.photos}>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-3">
                {["face", "boxer_front", "boxer_back", "slippers_front", "slippers_back"].map((k) => (
                  <div key={k} className="bg-[#0a0a0a] border border-white/5 overflow-hidden">
                    <div className="aspect-[3/4]">
                      {imgs[k] ? <img data-testid={`detail-photo-${k}`} src={imgs[k]} alt={k} className="w-full h-full object-cover" /> :
                        <div className="w-full h-full flex items-center justify-center text-xs text-neutral-600">—</div>}
                    </div>
                    <div className="label-eyebrow text-center py-2 px-1">{k.replace(/_/g, " ")}</div>
                  </div>
                ))}
              </div>
            </Info>

            <Info label="Introduction">
              <p className="text-neutral-300 leading-relaxed mt-3 whitespace-pre-wrap">{app.introduction || "—"}</p>
            </Info>
          </div>

          <div className="lg:col-span-5 space-y-px bg-white/5">
            {[
              ["Email", app.email],
              ["WhatsApp", app.whatsapp],
              ["Address", `${app.street} ${app.street_number}, ${app.city}, ${app.postal_code}, ${app.country}`],
              ["Instagram", app.instagram || "—"],
              ["OnlyFans", app.onlyfans || "—"],
              ["TikTok", app.tiktok || "—"],
              ["Height / Weight", `${app.height_cm}cm / ${app.weight_kg}kg`],
              ["Chest", app.chest_cm ? `${app.chest_cm}cm` : "—"],
              ["Waist", app.waist_cm ? `${app.waist_cm}cm` : "—"],
              ["Hips", app.hips_cm ? `${app.hips_cm}cm` : "—"],
              ["Arms / Thighs", `${app.arms_cm || "—"} / ${app.thighs_cm || "—"}`],
              ["Underwear Size", app.underwear_size],
              ["Body Type", app.body_type],
              ["Role", app.role],
              ["Couple Content", app.couple_content],
              ["Sexual Orientation", app.sexual_orientation || "—"],
              ["Followers", app.followers || "—"],
              ["Monthly Views", app.monthly_views || "—"],
              ["Engagement", app.engagement || "—"],
              ["Availability", app.availability || "—"],
              ["Experience", app.experience || "—"],
            ].map(([k, v]) => (
              <div key={k} className="bg-[#050505] flex justify-between gap-4 px-5 py-3">
                <span className="label-eyebrow flex-shrink-0">{k}</span>
                <span className="text-neutral-300 text-sm text-right">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const Info = ({ label, children }) => (
  <div>
    <div className="label-eyebrow text-[#00E5FF]">{label}</div>
    {children}
  </div>
);
